import { Check, XCircle } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Channel } from '../../../../shared-src/channel'
import { getFeathersClient } from './feathers-client'
import { getColorSet } from '@/theme/color'
import { useUserStore } from '@/state/user-store'
import { useChatStore } from '@/state/chat-store'

interface DmStatusBannerProps {
	channel: Channel
}

export function DmStatusBanner({ channel }: DmStatusBannerProps) {
	const [isApprovingDm, setIsApprovingDm] = useState(false)
	const [isRejectingDm, setIsRejectingDm] = useState(false)
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
	const user = useUserStore(state => state.user)

	const dmStatus = channel.dmStatus
	if (!dmStatus) return null

	const colorSet = getColorSet({ senderColor: user?.color })

	const handleApproveDm = async () => {
		if (!channel.dmStatus?.theirUserId) return

		setIsApprovingDm(true)
		try {
			const feathersClient = getFeathersClient()
			const channelService = feathersClient.service('channels')
			await channelService.patch(channel.id, { action: 'approve-dm' })
		} catch (error) {
			console.error('Failed to approve DM:', error)
		} finally {
			setIsApprovingDm(false)
		}
	}

	const handleRejectDm = async () => {
		if (!channel.dmStatus?.theirUserId) return

		setIsRejectingDm(true)
		try {
			const feathersClient = getFeathersClient()
			const channelService = feathersClient.service('channels')
			await channelService.patch(channel.id, { action: 'reject-dm' })
			setRejectDialogOpen(false)
			//	rejected DMs are filtered out by the backend on subsequent fetches.
			//	mirror that locally so the banner disappears and we don't try to refetch a now-hidden channel.
			useChatStore.getState().removeChannel(channel.id)
		} catch (error) {
			console.error('Failed to reject DM:', error)
		} finally {
			setIsRejectingDm(false)
		}
	}

	if (dmStatus.myStatus === 'pending') {
		const theirName = Object.values(channel.details?.membersByUserId ?? {})
			.find(m => m.userId === dmStatus.theirUserId)?.name || dmStatus.theirUserId || 'Someone'

		return (
			<div className="px-4 py-3 border-b" style={{
				backgroundColor: 'white',
				borderColor: colorSet.border
			}}>
				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
					<p className="text-sm" style={{ color: colorSet.dark }}>
						{theirName} wants to start a DM with you.
					</p>
					<div className="flex gap-2 w-full sm:w-auto">
						<Button
							variant="default"
							size="sm"
							className="flex-1 sm:flex-initial"
							onClick={handleApproveDm}
							disabled={isApprovingDm}
						>
							<Check className="mr-2 h-4 w-4" />
							{isApprovingDm ? 'Accepting...' : 'Accept'}
						</Button>
						<Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
							<DialogTrigger asChild>
								<Button variant="destructive" size="sm" className="flex-1 sm:flex-initial">
									<XCircle className="mr-2 h-4 w-4" />
									Reject
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Reject DM</DialogTitle>
									<DialogDescription>
										Are you sure you want to reject this DM request? This is permanent: the other user won't be able to request a DM with you again, only you can start a DM with them from a shared group.
									</DialogDescription>
								</DialogHeader>
								<DialogFooter>
									<Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
										Cancel
									</Button>
									<Button variant="destructive" onClick={handleRejectDm} disabled={isRejectingDm}>
										{isRejectingDm ? 'Rejecting...' : 'Reject DM'}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</div>
				</div>
			</div>
		)
	}

	if (dmStatus.myStatus === 'accepted' && dmStatus.theirStatus === 'pending') {
		const theirName = Object.values(channel.details?.membersByUserId ?? {})
			.find(m => m.userId === dmStatus.theirUserId)?.name || 'the other user'

		return (
			<div className="px-4 py-3 border-b" style={{
				backgroundColor: 'white',
				borderColor: colorSet.border
			}}>
				<p className="text-sm text-center" style={{ color: colorSet.dark }}>
					Waiting for {theirName} to accept your DM request.
				</p>
			</div>
		)
	}

	return null
}